import {
  type DocumentParentType,
  type FileDocumentResponse,
  type PrepareDocumentUploadPayload,
  PrepareDocumentUploadPayloadSchema,
} from "@mytour/shared";

export interface DocumentUploadScope {
  companyId: string;
  parentType: DocumentParentType;
  parentId: string;
}

export function documentUploadManifestKey(scope: DocumentUploadScope): string {
  return `document-uploads:${JSON.stringify([scope.companyId, scope.parentType, scope.parentId])}`;
}

export function readDocumentUploadManifest(
  scope: DocumentUploadScope
): PrepareDocumentUploadPayload[] {
  const prefix = `${documentUploadManifestKey(scope)}:`;
  const payloads: PrepareDocumentUploadPayload[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (!key?.startsWith(prefix)) {
      continue;
    }
    const raw = localStorage.getItem(key);
    if (!raw) {
      continue;
    }
    const payload = PrepareDocumentUploadPayloadSchema.parse(JSON.parse(raw));
    if (
      payload.parentId !== scope.parentId ||
      payload.parentType !== scope.parentType ||
      key !== `${prefix}${payload.uploadId}`
    ) {
      throw new Error("Manifeste d'envoi incompatible");
    }
    payloads.push(payload);
  }
  return payloads;
}

export function saveDocumentUploadManifest(
  scope: DocumentUploadScope,
  payloads: PrepareDocumentUploadPayload[]
): void {
  for (const payload of payloads) {
    localStorage.setItem(
      `${documentUploadManifestKey(scope)}:${payload.uploadId}`,
      JSON.stringify(payload)
    );
  }
}

export function completeDocumentUploadManifest(
  scope: DocumentUploadScope,
  uploadIds: string[]
): void {
  for (const uploadId of uploadIds) {
    localStorage.removeItem(`${documentUploadManifestKey(scope)}:${uploadId}`);
  }
}

export function documentUploadPayload(
  document: FileDocumentResponse
): PrepareDocumentUploadPayload | null {
  const result = PrepareDocumentUploadPayloadSchema.safeParse({
    uploadId: document.id,
    parentType: document.parentType,
    parentId: document.parentId,
    filename: document.originalFilename,
    mimeType: document.mimeType,
    sizeBytes: document.sizeBytes,
    checksumSha256: document.uploadChecksumSha256,
  });
  return result.success ? result.data : null;
}

export async function documentFileChecksum(file: File): Promise<string> {
  const bytes = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsArrayBuffer(file);
  });
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
